<template>
  <div class="content">
    <div>
      <div class="browser mt-5">
        <div class="browser-inner">
          <form @submit.prevent="submit">
            <div class="form-heading">
              <p>Page Title:</p>
            </div>

            <div class="mb-2">
              <t-editable-content
                data-test-id="global-block-page-title"
                v-model="form.title"
                :max-length="60"
                @updated="onTextChange"
              />
            </div>

            <div class="form-heading">
              <p>Logo:</p>
            </div>

            <div
              class="logo-container dashed-border mb-4 text-center"
              @click="chooseFile()"
              @mouseover="editHover.img = true"
              @mouseleave="editHover.img = false"
            >
              <b-form-file
                plain
                class="d-none"
                id="blockpage-logo-input"
                ref="file-image"
                :accept="imageUploaderRules.supportedTypes"
                variant="secondary"
                data-test-id="global-block-page-logo"
                :state="Boolean(form.logo)"
                @change="uploadLogo"
              ></b-form-file>
              <img :src="logoPreview" alt="blockpage-logo-preview" />
              <b-icon
                icon="pencil"
                class="edit mr-3 my-0 py-0"
                aria-hidden="true"
              ></b-icon>
            </div>

            <div
              class="mb-4"
              @mouseover="editHover.access_denied_message = true"
              @mouseleave="editHover.access_denied_message = false"
            >
              <div class="form-heading">
                <p>Access Denied Message:</p>
              </div>

              <t-editable-content
                v-model="form.access_denied_message"
                data-test-id="global-block-page-access_denied_message"
                :max-length="140"
                @updated="onTextChange"
              />
            </div>

            <div class="form-heading">
              <p>Domain:</p>
            </div>
            <h4 class="text-center text-break mb-4">
              <span class="preview__text--nonedit"> {{ exampleURL }}</span>
            </h4>

            <div class="form-heading">
              <p>Location IP:</p>
            </div>

            <p class="text-center mb-0">
              <span class="preview__text--nonedit">
                {{ $t("Location IP") }}: {{ locationIp }}</span
              >
            </p>
            <div
              class="mb-3"
              @mouseover="editHover.footer = true"
              @mouseleave="editHover.footer = false"
            >
              <div class="form-heading">
                <p>Footer:</p>
              </div>

              <t-editable-content
                data-test-id="global-block-page-footer"
                v-model="form.footer"
                :max-length="140"
                @updated="onTextChange"
              />
            </div>
          </form>
        </div>

        <section class="browser-wrap">
          <div class="preview">
            <img
              class="navbar-logo"
              :class="[{ hoverEdit: editHover.img }]"
              :src="logoPreview"
              alt="Titian Web logo"
            />

            <div class="preview__text">
              <p :class="[{ hoverEdit: editHover.access_denied_message }]">
                <strong>{{ this.form.access_denied_message }}</strong>
              </p>
              <p>
                <strong>{{ $t("Domain") }}:</strong>
                <span> {{ $t("http://example.com/") }}</span>
              </p>
              <p>
                <strong>{{ $t("Location IP") }}</strong
                >: 0.0.0.0
              </p>
              <p>
                <strong>{{ $t("The content is categorized under") }}:</strong>
                {{ $t("Gambling") }}
              </p>
              <p>
                {{
                  $t(
                    "Please contact your network admin if you think you should have access to this content"
                  )
                }}
              </p>
            </div>
          </div>
          <div
            class="browser-footer"
            :class="[{ hoverEdit: editHover.footer }]"
          >
            <p>{{ form.footer }}</p>
          </div>
        </section>
      </div>
      <t-save-button
        data-test-id="block-page-save"
        :form-model="$v.form"
        :fetching="fetching"
        @submit="onSubmit"
      >
        <b-button class="btn-tertiary mr-3" @click="onReset" v-show="!fetching">
          {{ $t("Reset") }}
        </b-button>
      </t-save-button>
    </div>
  </div>
</template>

<route>
{
  "name": "Customer Block Page"
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";
import { required } from "vuelidate/lib/validators";
import dayjs from "dayjs";

export default {
  layout: "customer",
  name: "blockPage",

  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("blockPage", [
      "fetchCustomerBlockPage",
      "updateCustomerBlockPage",
      "resetCustomerBlockPage",
      "updateCustomerBlockPageForm",
    ]),
    ...mapActions("toast", ["displayToast"]),

    updateFormData(el) {
      const targetEl = el.srcElement;
      this.form[targetEl.dataset.el] = targetEl.innerText;
    },

    onTextChange() {
      this.$v.$touch();
    },

    chooseFile() {
      document.getElementById("blockpage-logo-input").click();
    },
    uploadLogo(event) {
      this.fetching = true;
      try {
        const image = event.target.files[0];
        const reader = new FileReader();
        reader.readAsDataURL(image);
        reader.onload = (event) => {
          const file = this.$refs["file-image"].files[0];
          this.uploadedImage.size = file.size;
          let img = new Image();
          img.src = window.URL.createObjectURL(file);
          img.onload = () => {
            this.uploadedImage.height = img.height;
            this.uploadedImage.width = img.width;
            if (
              img.height > this.imageUploaderRules.maxHeight ||
              img.width > this.imageUploaderRules.maxWidth
            ) {
              this.displayToast({
                title: this.$t("Error"),
                message: this.$t("The image dimensions are too big."),
                duration: 2000,
                variant: "danger",
              });
              this.fetching = false;
              return;
            }
          };
          if (file.size > this.imageUploaderRules.maxSize) {
            this.displayToast({
              title: this.$t("Error"),
              message: this.$t("File size exceeds maximum of 1mb."),
              duration: 2000,
              variant: "danger",
            });
            this.fetching = false;
            return;
          }
          this.form.logo = image;
          this.logoPreview = event.target.result;
          this.fetching = false;
        };
        this.$v.$touch();
      } catch (err) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("Something went wrong."),
          duration: 2000,
          variant: "danger",
        });
      }
    },

    async onReset() {
      this.fetching = true;
      try {
        await this.resetCustomerBlockPage({
          user_id: this.selectedUser.id,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Reset",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
      }
    },

    async onSubmit() {
      this.fetching = true;
      if (
        this.uploadedImage.height > this.imageUploaderRules.maxHeight ||
        this.uploadedImage.width > this.imageUploaderRules.maxWidth
      ) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("The image dimensions are too big."),
          duration: 2000,
          variant: "danger",
        });
        this.fetching = false;
        return;
      }
      if (this.uploadedImage.size > this.imageUploaderRules.maxSize) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("File size exceeds maximum of 1mb."),
          duration: 2000,
          variant: "danger",
        });
        this.fetching = false;
        return;
      }

      try {
        if (!this.form.logo) {
          await this.updateCustomerBlockPage({
            user_id: this.selectedUser.id,
            body: this.form,
          });
        } else {
          const body = new FormData();
          if (this.form.logo !== this.logoPreview)
            body.append("logo", this.form.logo);
          body.append("access_denied_message", this.form.access_denied_message);
          body.append("footer", this.form.footer);
          body.append("title", this.form.title);

          document.title = this.form.title;

          await this.updateCustomerBlockPageForm({
            user_id: this.selectedUser.id,
            body,
          });
        }
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
      }
    },
  },
  computed: {
    ...mapGetters("blockPage", ["customerBlockPage"]),
    ...mapGetters("authentication", ["selectedUser"]),
  },

  data() {
    return {
      loading: true,
      fetching: false,
      editHover: {
        img: false,
        access_denied_message: false,
        footer: false,
      },
      open: false,
      exampleURL: this.$t("http://example.com/"),
      locationIp: "0.0.0.0",
      show: false,
      titleInputFocus: false,
      accessDeniedInputFocus: false,
      cloudKey: "",
      logoPreview: require("@/assets/icons/lock.svg"),
      imageUploaderRules: {
        maxSize: 1048576,
        maxWidth: 800,
        maxHeight: 800,
        supportedTypes: "image/*",
      },
      uploadedImage: {
        size: null,
        width: null,
        height: null,
      },
      currentYear: "",
      form: {
        title: "",
        access_denied_message: "",
        footer: `©2006-${this.currentYear} Copperfasten Technologies`,
        logo: require(`@/assets/icons/lock.svg`),
      },
    };
  },
  validations: {
    form: {
      title: {
        required,
      },
      access_denied_message: {},
      footer: {},
    },
  },
  watch: {
    customerBlockPage: {
      handler() {
        this.form.title = this.customerBlockPage.title;
        this.form.footer =
          this.customerBlockPage.footer ||
          `©2006-${this.currentYear} Copperfasten Technologies`;
        this.form.logo = this.customerBlockPage.logo;
        this.form.access_denied_message =
          this.customerBlockPage.access_denied_message;

        this.logoPreview =
          this.customerBlockPage.logo || require("@/assets/icons/lock.svg");
        this.form.access_denied_message =
          this.customerBlockPage.access_denied_message;
      },
    },
  },
  async created() {
    this.currentYear = dayjs().year();
    this.setPageTitle(this.$t("Block Page"));
    this.setPageSubtitle([
      this.$t(
        "A block page displays when a user tries to access a website that is blocked by their policy. Your block page is fully customizable, just click each box to edit the content."
      ),
    ]);
    try {
      await this.fetchCustomerBlockPage({
        user_id: this.selectedUser.id,
      });
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.variable-input-container {
  > * {
    width: max-content;
  }
}

.browser {
  background-color: rgb(227, 229, 233);
  border-radius: 0.2rem;
  margin-bottom: 5rem;
  padding: 0.1rem;

  &-inner {
    background-color: white;
    padding: 0.1rem;

    .lock-symbol {
      width: 100%;
    }

    .logo-container {
      width: 20rem;
      max-width: 100%;
      margin: 0 auto;
      position: relative;

      svg {
        position: absolute;
        bottom: 1rem;
        left: 0.5rem;
      }

      img {
        padding: 1rem;
        max-width: 100%;
        height: auto;
      }
    }

    form {
      margin: 0 auto;
      max-width: 95%;

      .cloud-key {
        width: 386px;
        max-width: 100%;
      }
    }
  }
}
</style>
